import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { deleteJobSelection, getJobSelectionBySlug, getJobSelections, saveJobSelection } from "../../api/api";
import { useGlobalData } from '../../context/GlobalDataContext';

const createTableRow = () => ({ columnTitle: "", itemsText: "" });
const createVacancyRow = () => ({ post: "", totalPost: "", recruitmentEligibility: "" });
const createUsefulLinkRow = () => ({ linkName: "", linkUrl: "" });
const createPostRow = () => ({ title: "", description: "", code: "", website: "" });
const createSingleColumnDetailRow = () => ({ title: "", content: "" });

const createInitialForm = () => ({
  jobSlug: "",
  admissionSlug: "",
  posts: [createPostRow()],
  tableData: [createTableRow()],
  tableData2: [createTableRow()],
  vacancyDetails: [createVacancyRow()],
  usefulLinks: [createUsefulLinkRow()],
  singleColumnDetails: [createSingleColumnDetailRow()]
});

const mapSelectionToForm = (jobSelection) => ({
  jobSlug: jobSelection.jobSlug || "",
  admissionSlug: jobSelection.admissionSlug || "",
  posts: jobSelection.posts?.length
    ? jobSelection.posts.map((row) => ({
        title: row.title || "",
        description: row.description || "",
        code: row.code || "",
        website: row.website || ""
      }))
    : [createPostRow()],
  tableData: jobSelection.tableData?.length
    ? jobSelection.tableData.map((row) => ({
        columnTitle: row.columnTitle || "",
        itemsText: (row.columnItems || []).join("\n")
      }))
    : [createTableRow()],
  tableData2: jobSelection.tableData2?.length
    ? jobSelection.tableData2.map((row) => ({
        columnTitle: row.columnTitle || "",
        itemsText: (row.columnItems || []).join("\n")
      }))
    : [createTableRow()],
  vacancyDetails: jobSelection.vacancyDetails?.length
    ? jobSelection.vacancyDetails.map((row) => ({
        post: row.post || "",
        totalPost: row.totalPost || "",
        recruitmentEligibility: row.recruitmentEligibility || ""
      }))
    : [createVacancyRow()],
  usefulLinks: jobSelection.usefulLinks?.length
    ? jobSelection.usefulLinks.map((row) => ({
        linkName: row.linkName || "",
        linkUrl: row.linkUrl || ""
      }))
    : [createUsefulLinkRow()],
  singleColumnDetails: jobSelection.singleColumnDetails?.length
    ? jobSelection.singleColumnDetails.map((row) => ({
        title: row.title || "",
        content: row.content || ""
      }))
    : [createSingleColumnDetailRow()]
});

const buildPayload = (formData, selectedJob) => ({
  jobTitle: selectedJob.title,
  jobSlug: selectedJob.slug,
  admissionSlug: formData.admissionSlug,
  posts: formData.posts
    .map((row) => ({
      title: row.title.trim(),
      description: row.description.trim(),
      code: row.code.trim(),
      website: row.website.trim()
    }))
    .filter((row) => row.title || row.description || row.code || row.website),
  tableData: formData.tableData
    .map((row) => ({
      columnTitle: row.columnTitle.trim(),
      columnItems: row.itemsText.split("\n").map((item) => item.trim()).filter(Boolean)
    }))
    .filter((row) => row.columnTitle && row.columnItems.length > 0),
  tableData2: formData.tableData2
    .map((row) => ({
      columnTitle: row.columnTitle.trim(),
      columnItems: row.itemsText.split("\n").map((item) => item.trim()).filter(Boolean)
    }))
    .filter((row) => row.columnTitle && row.columnItems.length > 0),
  vacancyDetails: formData.vacancyDetails
    .map((row) => ({
      post: row.post.trim(),
      totalPost: row.totalPost.trim(),
      recruitmentEligibility: row.recruitmentEligibility.trim()
    }))
    .filter((row) => row.post && row.totalPost && row.recruitmentEligibility),
  usefulLinks: formData.usefulLinks
    .map((row) => ({
      linkName: row.linkName.trim(),
      linkUrl: row.linkUrl.trim()
    }))
    .filter((row) => row.linkName && row.linkUrl),
  singleColumnDetails: formData.singleColumnDetails
    .map((row) => ({
      title: row.title.trim(),
      content: row.content.trim()
    }))
    .filter((row) => row.title || row.content)
});

function RowControls({ onAdd, onRemove, canRemove }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", justifyContent: "flex-end" }}>
      <button type="button" onClick={onAdd}>Add Row</button>
      <button type="button" onClick={onRemove} disabled={!canRemove}>Remove</button>
    </div>
  );
}

function SelectionJobAdminPanel() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const syncDarkMode = () => setDarkMode(localStorage.getItem('darkMode') === 'true');
    window.addEventListener('storage', syncDarkMode);
    window.addEventListener('darkModeChanged', syncDarkMode);
    return () => {
      window.removeEventListener('storage', syncDarkMode);
      window.removeEventListener('darkModeChanged', syncDarkMode);
    };
  }, []);
  const formSectionRef = useRef(null);
  const { threeTable = {}, loading, fetchThreeTable } = useGlobalData();
  const jobs = threeTable.jobs || [];
  const admissions = threeTable.admissions || [];
  const [savedSelections, setSavedSelections] = useState([]);
  const [formData, setFormData] = useState(createInitialForm());
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  // loading now comes from context
  const role = localStorage.getItem('role');

  useEffect(() => {
    if (!threeTable?.jobs?.length || !threeTable?.admissions?.length) {
      fetchThreeTable();
    }
  }, [threeTable?.jobs?.length, threeTable?.admissions?.length, fetchThreeTable]);

  const loadSelections = async () => {
    try {
      const selectionsRes = await getJobSelections();
      setSavedSelections(selectionsRes.data || []);
      setError("");
    } catch (loadError) {
      setError(loadError.response?.data?.error || loadError.message || "Unable to load selection jobs");
    }
  };

  React.useEffect(() => {
    loadSelections();
    // Listen for global data refresh
    const handler = () => loadSelections();
    window.addEventListener('dataChanged', handler);
    return () => window.removeEventListener('dataChanged', handler);
  }, []);

  if (role !== 'superadmin') {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: '2rem' }}>Access denied: Superadmin only</div>;
  }

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const updateListField = (section, index, field, value) => {
    setFormData((current) => ({
      ...current,
      [section]: current[section].map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    }));
  };


  // Add row at the end (default behavior)
  const addRow = (section, factory) => {
    setFormData((current) => ({ ...current, [section]: [...current[section], factory()] }));
  };

  // Insert row at a specific index (for tableData/tableData2)
  const insertRowAt = (section, index, factory) => {
    setFormData((current) => {
      const rows = [...current[section]];
      rows.splice(index + 1, 0, factory());
      return { ...current, [section]: rows };
    });
  };

  const removeRow = (section, index, factory) => {
    setFormData((current) => {
      const nextRows = current[section].filter((_, itemIndex) => itemIndex !== index);
      return { ...current, [section]: nextRows.length ? nextRows : [factory()] };
    });
  };

  const handleSelectJob = async (jobSlug) => {
    updateField("jobSlug", jobSlug);
    setMessage("");
    setError("");

    if (!jobSlug) {
      setFormData(createInitialForm());
      return;
    }

    const existing = savedSelections.find((item) => item.jobSlug === jobSlug);
    if (!existing) {
      const matchedAdmission = admissions.find((item) => (item.jobSlug || item.slug) === jobSlug);
      setFormData({
        ...createInitialForm(),
        jobSlug,
        admissionSlug: matchedAdmission?.slug || ""
      });
      return;
    }

    try {
      const matched = await getJobSelectionBySlug(jobSlug);
      setFormData(mapSelectionToForm(matched.data));
      setMessage("Saved detail database se load ho gaya.");
    } catch (fetchError) {
      setError(fetchError.response?.data?.error || fetchError.message || "Unable to fetch saved detail");
    }
  };

  const handleSelectAdmission = (admissionSlug) => {
    const selectedAdmission = admissions.find((item) => item.slug === admissionSlug);
    const resolvedJobSlug = selectedAdmission?.jobSlug || selectedAdmission?.slug || "";

    setFormData((current) => ({
      ...current,
      jobSlug: resolvedJobSlug || current.jobSlug,
      admissionSlug
    }));
  };

  const filteredAdmissions = formData.jobSlug
    ? admissions.filter((item) => (item.jobSlug || item.slug) === formData.jobSlug)
    : admissions;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    try {
      const selectedAdmission = admissions.find((item) => item.slug === formData.admissionSlug);
      const resolvedJobSlug = formData.jobSlug || selectedAdmission?.jobSlug || selectedAdmission?.slug || "";
      const selectedJob = jobs.find((job) => job.slug === resolvedJobSlug);
      const resolvedEntity = selectedJob || (selectedAdmission
        ? {
            slug: selectedAdmission.slug || resolvedJobSlug,
            title: selectedAdmission.title || resolvedJobSlug
          }
        : null);

      if (!resolvedEntity) {
        throw new Error("Pehle Select Job ya Select Admission me se koi ek choose kijiye.");
      }

      await saveJobSelection(
        buildPayload(
          {
            ...formData,
            jobSlug: resolvedEntity.slug,
            admissionSlug: formData.admissionSlug || selectedAdmission?.slug || ""
          },
          resolvedEntity
        )
      );
      setMessage("Selection job detail save ho gaya.");
      window.dispatchEvent(new Event('dataChanged'));
    } catch (submitError) {
      setError(submitError.response?.data?.error || submitError.message || "Unable to save selection job");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Is selection job detail ko delete karna hai?")) {
      return;
    }

    try {
      await deleteJobSelection(id);
      if (savedSelections.find((item) => item._id === id)?.jobSlug === formData.jobSlug) {
        setFormData(createInitialForm());
      }
      window.dispatchEvent(new Event('dataChanged'));
    } catch (deleteError) {
      setError(deleteError.response?.data?.error || deleteError.message || "Unable to delete selection job");
    }
  };

  const handleModify = async (jobSlug) => {
    await handleSelectJob(jobSlug);
    setMessage("Saved selection job detail form me load ho gaya. Ab change karke save kijiye.");
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className={`resource-admin-panel resource-admin-panel--blue${darkMode ? ' dark-mode' : ''}`}
      style={{
        backgroundColor: darkMode ? '#232836' : '#eef7ff',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px',
        border: `2px solid ${darkMode ? '#1976d2' : '#1976d2'}`
      }}
    >
      <h2 style={{ color: darkMode ? '#60a5fa' : '#0d47a1', marginTop: 0 }}>🧩 Selection Job Detail Admin Panel</h2>
      <p style={{ color: darkMode ? '#b0b8c9' : '#333', marginBottom: '16px' }}>Purane project ke /mform/:slug route ke liye naya database-driven page isi panel se manage hoga.</p>

      <form
        ref={formSectionRef}
        onSubmit={handleSubmit}
        style={{
          backgroundColor: darkMode ? '#181c24' : 'white',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <label>
            <span>Select Job</span>
            <select value={formData.jobSlug} onChange={(event) => handleSelectJob(event.target.value)} style={{ display: "block", width: "100%", marginTop: "6px", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}>
              <option value="">Select Job</option>
              {jobs.map((job) => (
                <option key={job._id} value={job.slug}>{job.title} ({job.slug})</option>
              ))}
            </select>
          </label>

          <label>
            <span>Select Admission</span>
            <select value={formData.admissionSlug} onChange={(event) => handleSelectAdmission(event.target.value)} style={{ display: "block", width: "100%", marginTop: "6px", padding: "10px", borderRadius: "4px", border: "1px solid #ddd" }}>
              <option value="">Select Admission</option>
              {filteredAdmissions.map((admission) => (
                <option key={admission._id} value={admission.slug}>{admission.title} ({admission.slug})</option>
              ))}
            </select>
          </label>
        </div>

        {[
          ["tableData", "Table Data", "Important Dates", createTableRow],
          ["posts", "Post Management", "", createPostRow],
          ["tableData2", "Table Data 2", "Application Fee", createTableRow]
        ].map(([section, title, placeholder, factory]) => (
          <div key={section} style={{ marginTop: "24px", borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
            <h3 style={{ color: "#0d47a1" }}>{title}</h3>
            {section === "posts"
              ? formData.posts.map((row, index) => (
                  <div key={`post-${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr 1fr auto", gap: "12px", marginBottom: "12px" }}>
                    <input value={row.title} onChange={(event) => updateListField("posts", index, "title", event.target.value)} placeholder="Post Title" />
                    <textarea value={row.description} onChange={(event) => updateListField("posts", index, "description", event.target.value)} placeholder="Description" style={{ minHeight: "90px" }} />
                    <input value={row.code} onChange={(event) => updateListField("posts", index, "code", event.target.value)} placeholder="Code" />
                    <input value={row.website} onChange={(event) => updateListField("posts", index, "website", event.target.value)} placeholder="Website URL" />
                    <RowControls onAdd={() => addRow("posts", createPostRow)} onRemove={() => removeRow("posts", index, createPostRow)} canRemove={formData.posts.length > 1} />
                  </div>
                ))
              : <>
                  {formData[section].map((row, index) => (
                    <div key={`${section}-${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 90px", gap: "12px", marginBottom: "12px" }}>
                      <input value={row.columnTitle} onChange={(event) => updateListField(section, index, "columnTitle", event.target.value)} placeholder={placeholder} />
                      <textarea value={row.itemsText} onChange={(event) => updateListField(section, index, "itemsText", event.target.value)} placeholder={"Line 1\nLine 2"} style={{ minHeight: "90px" }} />
                      <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                        <button type="button" onClick={() => insertRowAt(section, index, factory)} style={{height:'44px', minWidth:'90px', fontSize:'13px', padding:'0 8px'}}>Insert</button>
                        <button type="button" onClick={() => removeRow(section, index, factory)} disabled={formData[section].length <= 1} style={{height:'38px'}}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: '8px' }}>
                    <button type="button" onClick={() => addRow(section, factory)}>
                      Add Row
                    </button>
                  </div>
                </>}
          </div>
        ))}

        <div style={{ marginTop: "24px", borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
          <h3 style={{ color: "#0d47a1" }}>Vacancy Details</h3>
          {formData.vacancyDetails.map((row, index) => (
            <div key={`vacancy-${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr 90px", gap: "12px", marginBottom: "12px" }}>
              <input value={row.post} onChange={(event) => updateListField("vacancyDetails", index, "post", event.target.value)} placeholder="Post" />
              <input value={row.totalPost} onChange={(event) => updateListField("vacancyDetails", index, "totalPost", event.target.value)} placeholder="Total Post" />
              <textarea value={row.recruitmentEligibility} onChange={(event) => updateListField("vacancyDetails", index, "recruitmentEligibility", event.target.value)} placeholder="Eligibility" style={{ minHeight: "90px" }} />
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                <button type="button" onClick={() => insertRowAt("vacancyDetails", index, createVacancyRow)} style={{height:'44px', minWidth:'90px', fontSize:'13px', padding:'0 8px'}}>Insert</button>
                <button type="button" onClick={() => removeRow("vacancyDetails", index, createVacancyRow)} disabled={formData.vacancyDetails.length <= 1} style={{height:'38px'}}>Remove</button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: '8px' }}>
            <button type="button" onClick={() => addRow("vacancyDetails", createVacancyRow)}>
              Add Row
            </button>
          </div>
        </div>

        <div style={{ marginTop: "24px", borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
          <h3 style={{ color: "#0d47a1" }}>Useful Links</h3>
          {formData.usefulLinks.map((row, index) => (
            <div key={`link-${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 90px", gap: "12px", marginBottom: "12px" }}>
              <input value={row.linkName} onChange={(event) => updateListField("usefulLinks", index, "linkName", event.target.value)} placeholder="Link Name" />
              <input value={row.linkUrl} onChange={(event) => updateListField("usefulLinks", index, "linkUrl", event.target.value)} placeholder="https://example.com" />
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                <button type="button" onClick={() => insertRowAt("usefulLinks", index, createUsefulLinkRow)} style={{height:'44px', minWidth:'90px', fontSize:'13px', padding:'0 8px'}}>Insert</button>
                <button type="button" onClick={() => removeRow("usefulLinks", index, createUsefulLinkRow)} disabled={formData.usefulLinks.length <= 1} style={{height:'38px'}}>Remove</button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: '8px' }}>
            <button type="button" onClick={() => addRow("usefulLinks", createUsefulLinkRow)}>
              Add Row
            </button>
          </div>
        </div>

        <div style={{ marginTop: "24px", borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
          <h3 style={{ color: "#0d47a1" }}>Single Column Two Row Data</h3>
          {formData.singleColumnDetails.map((row, index) => (
            <div key={`single-column-${index}`} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 90px", gap: "12px", marginBottom: "12px" }}>
              <input value={row.title} onChange={(event) => updateListField("singleColumnDetails", index, "title", event.target.value)} placeholder="Bold Title" />
              <textarea value={row.content} onChange={(event) => updateListField("singleColumnDetails", index, "content", event.target.value)} placeholder="Normal content" style={{ minHeight: "90px" }} />
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                <button type="button" onClick={() => insertRowAt("singleColumnDetails", index, createSingleColumnDetailRow)} style={{height:'44px', minWidth:'90px', fontSize:'13px', padding:'0 8px'}}>Insert</button>
                <button type="button" onClick={() => removeRow("singleColumnDetails", index, createSingleColumnDetailRow)} disabled={formData.singleColumnDetails.length <= 1} style={{height:'38px'}}>Remove</button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: '8px' }}>
            <button type="button" onClick={() => addRow("singleColumnDetails", createSingleColumnDetailRow)}>
              Add Row
            </button>
          </div>
        </div>

        {message && <div style={{ marginTop: '16px', padding: '12px', borderRadius: '6px', backgroundColor: darkMode ? '#1a2e22' : '#e8f5e9', color: darkMode ? '#7ee787' : '#2e7d32' }}>{message}</div>}
        {error && <div style={{ marginTop: '16px', padding: '12px', borderRadius: '6px', backgroundColor: darkMode ? '#2e1a1a' : '#ffebee', color: darkMode ? '#ff7b72' : '#c62828' }}>{error}</div>}

        <button
          type="submit"
          style={{
            width: '100%',
            marginTop: '18px',
            padding: '12px',
            backgroundColor: darkMode ? '#1976d2' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Save Selection Job Detail
        </button>
      </form>

      <h3 style={{ color: darkMode ? '#60a5fa' : '#0d47a1' }}>Saved Selection Job Details ({savedSelections.length})</h3>
      {savedSelections.length === 0 && !loading && (
        <p style={{ color: darkMode ? '#b0b8c9' : '#666' }}>Abhi tak koi selection detail save nahi hui hai.</p>
      )}
      {savedSelections.length > 0 && (
        <div style={{ display: 'grid', gap: '12px', maxHeight: '980px', overflowY: savedSelections.length > 10 ? 'auto' : 'visible', paddingRight: savedSelections.length > 10 ? '6px' : 0, position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: darkMode ? 'rgba(24,28,36,0.85)' : 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              fontWeight: 'bold',
              fontSize: 18,
              color: darkMode ? '#60a5fa' : '#1976d2',
              borderRadius: '8px'
            }}>
              Loading saved details...
            </div>
          )}
          {savedSelections.map((item) => {
            // Show status as 'Complete' if published, else 'Pending'
            const isComplete = !!item.published;
            const statusLabel = isComplete ? 'Complete' : 'Pending';
            const statusColor = isComplete ? (darkMode ? '#388e3c' : '#43a047') : (darkMode ? '#fbc02d' : '#fbc02d');
            const statusBg = isComplete ? (darkMode ? '#1a2e22' : '#e8f5e9') : (darkMode ? '#2e2a1a' : '#fffde7');
            const statusTextColor = isComplete ? (darkMode ? '#7ee787' : '#2e7d32') : (darkMode ? '#ffe066' : '#b26a00');

            return (
              <div
                key={item._id}
                style={{
                  backgroundColor: darkMode ? '#181c24' : 'white',
                  border: `1px solid ${darkMode ? '#60a5fa' : '#ddd'}`,
                  borderRadius: '8px',
                  padding: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'center',
                  opacity: loading ? 0.5 : 1
                }}
              >
                <div>
                  <strong style={{ color: darkMode ? '#fff' : undefined }}>{item.jobTitle}</strong>
                  <div style={{ color: darkMode ? '#b0b8c9' : '#666', marginTop: '4px' }}>{item.jobSlug}</div>
                  <div style={{ marginTop: 6 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 12px',
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: 13,
                        backgroundColor: statusBg,
                        color: statusTextColor,
                        border: `1.5px solid ${statusColor}`,
                        marginTop: 2
                      }}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => handleModify(item.jobSlug)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      backgroundColor: darkMode ? '#232836' : '#fff7ed',
                      color: darkMode ? '#fbbf24' : '#c2410c',
                      border: `1px solid ${darkMode ? '#fbbf24' : '#fdba74'}`,
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Modify
                  </button>
                  <Link
                    to={`/mform/${item.jobSlug}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      backgroundColor: darkMode ? '#232836' : '#e3f2fd',
                      color: darkMode ? '#60a5fa' : '#0d47a1',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    View Page
                  </Link>
                  <button
                    className="admin-delete-button"
                    type="button"
                    onClick={() => handleDelete(item._id)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      backgroundColor: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SelectionJobAdminPanel;
